<template>
  <!-- Encabezado de la tienda. HTML + CSS plano (sin v-toolbar / v-menu / v-text-field) para que funcione igual en Windows 7. -->
  <header class="ps-header">
    <!-- BARRA SUPERIOR DE PROMOCIÓN -->
    <div v-if="!model.isAuthPage" class="ps-header__promo">
      <i class="mdi mdi-truck-fast-outline" aria-hidden="true"></i>
      <span>Envíos a todo el país<em>·</em>Comprá en cuotas<em>·</em>Cambios sin cargo</span>
    </div>

    <!-- BARRA PRINCIPAL -->
    <div v-if="!model.isAuthPage" class="ps-header__main">
      <a href="/Home/Index" class="ps-header__logo" aria-label="Prote Shop - Inicio">
        <img :src="logoUrl" alt="Prote Shop" width="110" height="46" />
      </a>

      <div class="ps-header__actions">
        <template v-if="model.isClientLoggedIn">
          <a href="/Account" class="ps-header__icon-btn" title="Mi cuenta" aria-label="Mi cuenta">
            <i class="mdi mdi-account-outline" aria-hidden="true"></i>
          </a>
        </template>

        <template v-else>
          <a href="/Home/Login" class="ps-header__btn ps-header__btn--outline">Iniciar sesión</a>
          <a href="/Home/Register" class="ps-header__btn ps-header__btn--soft ps-header__register">Registrarse</a>
        </template>

        <button type="button" class="ps-header__icon-btn" title="Carrito" aria-label="Carrito" @click="global.toggleCart()">
          <i class="mdi mdi-cart-outline" aria-hidden="true"></i>
          <span v-if="global.cartCount > 0" class="ps-header__badge">{{ global.cartCount }}</span>
        </button>
      </div>

      <form class="ps-header__search" role="search" @submit.prevent="doSearch">
        <button type="submit" class="ps-header__search-btn" aria-label="Buscar" title="Buscar"><i class="mdi mdi-magnify" aria-hidden="true"></i></button>
        <input v-model="search" type="search" placeholder="Buscar productos" aria-label="Buscar productos" autocomplete="off" />
      </form>
    </div>

    <!-- NAVEGACIÓN DE CATEGORÍAS -->
    <nav class="ps-header__nav" :class="{ 'ps-header__nav--plain': model.isAuthPage }" aria-label="Categorías">
      <a href="/Home/Index" class="ps-header__link">Inicio</a>

      <div ref="menuRoot" class="ps-header__dropdown" @mouseenter="openMenu" @mouseleave="closeMenu">
        <button
          type="button"
          class="ps-header__link ps-header__link--button"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-haspopup="true"
          @click="menuOpen = !menuOpen"
        >
          Categorías
          <i class="mdi mdi-chevron-down" aria-hidden="true"></i>
        </button>

        <ul v-show="menuOpen" class="ps-header__menu">
          <li v-for="categoria in model.categories" :key="categoria.value">
            <a :href="`/Home/Category?idCategory=${categoria.value}`">{{ categoria.title }}</a>
          </li>
        </ul>
      </div>

      <a href="/Home/Offer" class="ps-header__link ps-header__link--offer">Ofertas</a>
    </nav>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

const global = window.useGlobalStore();

const model = ref(window.HeaderData || { categories: [], isClientLoggedIn: false });

// ruta absoluta en una variable: si va literal en el template, Vite intenta resolverla como módulo
const logoUrl = "/assets/logo.svg";
const search = ref("");
const menuOpen = ref(false);
const menuRoot = ref(null);

function doSearch() {
  if (!search.value?.trim()) return;
  window.location.href = "/Home/Search?q=" + encodeURIComponent(search.value.trim());
}

// En pantallas con mouse el menú se abre al pasar; en táctiles, al tocar. Se cierra al tocar afuera o con Esc.
function openMenu() {
  if (window.matchMedia && window.matchMedia("(hover: hover)").matches) menuOpen.value = true;
}
function closeMenu() {
  if (window.matchMedia && window.matchMedia("(hover: hover)").matches) menuOpen.value = false;
}
function onDocumentClick(e) {
  if (menuRoot.value && !menuRoot.value.contains(e.target)) menuOpen.value = false;
}
function onKey(e) {
  if (e.key === "Escape") menuOpen.value = false;
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onKey);
});
</script>

<style scoped>
.ps-header {
  position: relative;
  z-index: 100;
  background: #f4efe6;
  box-shadow: 0 2px 6px rgba(26, 26, 0, 0.12);
  color: #1a1a00;
}

/* ---------- Promo ---------- */
.ps-header__promo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 4px 12px;
  background: #124b4e;
  color: #ffffff;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;
}

.ps-header__promo em {
  margin: 0 8px;
  font-style: normal;
  opacity: 0.7;
}

/* ---------- Barra principal: celular = logo + acciones / buscador ---------- */
.ps-header__main {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    "logo actions"
    "search search";
  align-items: center;
  row-gap: 8px;
  column-gap: 12px;
  padding: 8px 16px;
}

.ps-header__logo {
  grid-area: logo;
  display: flex;
  align-items: center;
}

.ps-header__logo img {
  display: block;
  width: 110px;
  height: 46px;
  object-fit: contain;
}

.ps-header__actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.ps-header__btn {
  display: inline-block;
  padding: 7px 14px;
  border: 1.5px solid #124b4e;
  border-radius: 8px;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease;
}

.ps-header__btn--outline {
  background: transparent;
  color: #124b4e;
}

.ps-header__btn--outline:hover {
  background: rgba(18, 75, 78, 0.14);
}

.ps-header__btn--soft {
  background: rgba(18, 75, 78, 0.18);
  color: #124b4e;
}

.ps-header__btn--soft:hover {
  background: rgba(18, 75, 78, 0.3);
}

/* en celulares chicos sólo entra "Iniciar sesión" (Registrarse está en la pantalla de login) */
.ps-header__register {
  display: none;
}

.ps-header__icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #1a1a00;
  font-size: 24px;
  text-decoration: none;
  cursor: pointer;
}

.ps-header__icon-btn:hover {
  background: rgba(26, 26, 0, 0.07);
}

.ps-header__badge {
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 999px;
  background: #124b4e;
  color: #f4efe6;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.ps-header__search {
  grid-area: search;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 14px;
  border-radius: 10px;
  background: #e3dbce;
}

.ps-header__search-btn {
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.ps-header__search .mdi {
  font-size: 22px;
  color: rgba(26, 26, 0, 0.6);
}

.ps-header__search input {
  flex: 1 1 auto;
  min-width: 0;
  height: 42px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #1a1a00;
  font-family: inherit;
  font-size: 15px;
}

.ps-header__search input::placeholder {
  color: rgba(26, 26, 0, 0.55);
}

.ps-header__search:focus-within {
  box-shadow: 0 0 0 2px #124b4e;
}

/* ---------- Navegación ---------- */
.ps-header__nav {
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 0 16px;
  border-top: 1px solid rgba(26, 26, 0, 0.1);
}

.ps-header__nav--plain {
  border-top: 0;
}

.ps-header__link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 12px 0;
  border: 0;
  background: transparent;
  color: #1a1a00;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.ps-header__link:hover {
  color: #124b4e;
}

.ps-header__link--offer {
  color: #c0392b;
}

.ps-header__link--offer:hover {
  color: #e74c3c;
}

.ps-header__dropdown {
  position: relative;
}

.ps-header__menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 200;
  min-width: 200px;
  max-height: 60vh;
  margin: 0;
  padding: 6px 0;
  overflow-y: auto;
  list-style: none;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(26, 26, 0, 0.22);
}

.ps-header__menu a {
  display: block;
  padding: 10px 18px;
  color: #1a1a00;
  font-size: 14px;
  text-decoration: none;
}

.ps-header__menu a:hover {
  background: rgba(18, 75, 78, 0.14);
}

/* ---------- Tablet / escritorio ---------- */
@media (min-width: 600px) {
  .ps-header__register {
    display: inline-block;
  }
}

@media (min-width: 960px) {
  .ps-header__main {
    grid-template-columns: auto 1fr auto;
    grid-template-areas: "logo search actions";
    column-gap: 20px;
    padding: 8px 24px;
  }

  .ps-header__search {
    max-width: 560px;
  }

  .ps-header__nav {
    padding: 0 24px;
  }
}

/* ---------- Celulares ---------- */
@media (max-width: 599px) {
  .ps-header__promo {
    font-size: 11.5px;
  }

  .ps-header__promo em {
    margin: 0 4px;
  }

  .ps-header__main {
    padding: 8px 12px;
  }

  .ps-header__logo img {
    width: 96px;
    height: 40px;
  }

  .ps-header__nav {
    gap: 18px;
    padding: 0 12px;
  }
}
</style>
